import React from "react";
import Rating from "@mui/material/Rating";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";
import ListItemText from "@mui/material/ListItemText";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import WorkIcon from "@mui/icons-material/Work";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { getRightworkAPI } from "../requests/utilities/requests";
import { API_get_learning_modules } from "../../../constants";

// Reframes proficiency setup as a per-role "ladder": each role expands to show
// star levels 1..N, and each level lists the modules required to reach it. The
// rule it makes explicit (and the backend enforces) is cumulative — a level
// requires its own modules plus every level below it.
function RoleProficiencyThresholdsDisplay(props) {
  const maxStars = props.proficiencyStarsCount || 5;
  const roles = props.learningModulesByRole || [];

  const [expandedRoleId, setExpandedRoleId] = React.useState(null);
  const [allModules, setAllModules] = React.useState([]);

  // "+ Add modules" picker (a checklist menu anchored to the clicked button).
  const [addAnchor, setAddAnchor] = React.useState(null);
  const [addContext, setAddContext] = React.useState(null); // { roleId, level }
  const [pickerChecked, setPickerChecked] = React.useState([]);
  const [pickerSearch, setPickerSearch] = React.useState("");

  // Half-star levels (1.5, 2.5, …) the admin has revealed this session so they
  // can create them even when none exist yet. Persisted half-star data shows on
  // its own; this just gives an "Add modules" target for brand-new ones.
  const [revealedHalfLevels, setRevealedHalfLevels] = React.useState(
    () => new Set()
  );
  const [halfAnchor, setHalfAnchor] = React.useState(null);
  const [halfRoleId, setHalfRoleId] = React.useState(null);

  // All company modules — powers the picker and the "not in this ladder" tray.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getRightworkAPI(API_get_learning_modules, {
          company_id: props.homeCompany,
        });
        if (!cancelled && res.status === 200) {
          setAllModules(res.data.redux?.payload?.learning_modules || []);
        }
      } catch (err) {
        // Non-fatal: the ladder still renders from learningModulesByRole.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [props.homeCompany]);

  // role.modules_through is keyed by star rating -> [{ learning_module }]. Fold
  // it into { level -> [module] } plus the set of uuids already in the ladder.
  const getRoleLadder = (role) => {
    const byLevel = {};
    const assignedUuids = new Set();
    const mt = role?.modules_through || {};
    Object.keys(mt).forEach((key) => {
      // Use the exact star value — rounding would collapse legacy half-star
      // levels (e.g. 1.5) into the wrong bucket and lose the threshold.
      const level = Number(key);
      if (Number.isNaN(level)) return;
      (mt[key] || []).forEach((entry) => {
        const m = entry.learning_module;
        if (!m) return;
        if (!byLevel[level]) byLevel[level] = [];
        byLevel[level].push(m);
        assignedUuids.add(m.uuid);
      });
    });
    return { byLevel, assignedUuids };
  };

  const getRoleSummary = (role) => {
    const { byLevel, assignedUuids } = getRoleLadder(role);
    const levels = Object.keys(byLevel)
      .map(Number)
      .filter((l) => (byLevel[l] || []).length > 0);
    return {
      maxLevel: levels.length ? Math.max(...levels) : 0,
      levelCount: levels.length,
      moduleCount: assignedUuids.size,
    };
  };

  const getRevealedForRole = (roleId) =>
    Array.from(revealedHalfLevels)
      .filter((k) => k.startsWith(`${roleId}:`))
      .map((k) => Number(k.split(":")[1]))
      .filter((n) => !Number.isNaN(n));

  // Levels to render for a role: integer levels 1..N, any non-integer levels in
  // the data, and any half-star levels the admin has revealed this session.
  const getRenderedLevels = (role) => {
    const { byLevel } = getRoleLadder(role);
    return Array.from(
      new Set([
        ...Array.from({ length: maxStars }, (_, i) => i + 1),
        ...Object.keys(byLevel)
          .map(Number)
          .filter((n) => !Number.isNaN(n) && n > 0),
        ...getRevealedForRole(role.id),
      ])
    ).sort((a, b) => a - b);
  };

  // Half-steps (0.5, 1.5, …, maxStars-0.5) not already shown for this role.
  const getAvailableHalfSteps = (role) => {
    const rendered = getRenderedLevels(role);
    return Array.from({ length: maxStars }, (_, i) => i + 0.5).filter(
      (h) => !rendered.includes(h)
    );
  };

  const revealHalfLevel = (roleId, level) =>
    setRevealedHalfLevels((prev) => new Set(prev).add(`${roleId}:${level}`));

  const openAddPicker = (event, roleId, level) => {
    setAddAnchor(event.currentTarget);
    setAddContext({ roleId, level });
    setPickerChecked([]);
    setPickerSearch("");
  };

  const closeAddPicker = () => {
    setAddAnchor(null);
    setAddContext(null);
    setPickerChecked([]);
    setPickerSearch("");
  };

  const togglePick = (uuid) =>
    setPickerChecked((prev) =>
      prev.includes(uuid) ? prev.filter((u) => u !== uuid) : [...prev, uuid]
    );

  const submitAdd = () => {
    if (!addContext || pickerChecked.length === 0) return;
    props.post_learning_module_star_threshold(
      addContext.roleId,
      addContext.level,
      pickerChecked
    );
    closeAddPicker();
  };

  // Picker offers modules not already placed anywhere in this role's ladder.
  const pickerModules = React.useMemo(() => {
    if (!addContext) return [];
    const role = roles.find((r) => r.id === addContext.roleId);
    const assignedUuids = role
      ? getRoleLadder(role).assignedUuids
      : new Set();
    const query = pickerSearch.trim().toLowerCase();
    return (allModules || [])
      .filter((m) => !assignedUuids.has(m.uuid))
      .filter((m) => !query || (m.name || "").toLowerCase().includes(query));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addContext, allModules, roles, pickerSearch]);

  const renderLadder = (role) => {
    const { byLevel } = getRoleLadder(role);
    const levels = getRenderedLevels(role);
    const availableHalfSteps = getAvailableHalfSteps(role);

    return (
      <div className="border-t border-slate-100 bg-slate-50/60 px-5 py-4">
        <div className="space-y-2">
          {levels.map((level) => {
            const modules = byLevel[level] || [];
            return (
              <div
                key={level}
                className="flex items-start gap-4 rounded-lg border border-slate-200 bg-white px-4 py-3"
              >
                <div className="shrink-0 pt-1">
                  <Rating
                    value={level}
                    max={maxStars}
                    precision={0.5}
                    readOnly
                    size="small"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    {modules.length === 0 && (
                      <span className="text-xs text-slate-400">
                        No modules required for this level
                      </span>
                    )}
                    {modules.map((m) => (
                      <span
                        key={m.uuid}
                        className={`inline-flex items-center gap-1.5 rounded-lg ${
                          m.color || "bg-slate-600"
                        } px-2.5 py-1 text-xs font-medium text-white`}
                      >
                        {m.name}
                        <button
                          type="button"
                          onClick={() =>
                            props.delete_learning_module_star_threshold(
                              role.id,
                              m.uuid
                            )
                          }
                          className="rounded-full p-0.5 transition-colors hover:bg-black/20"
                          aria-label={`Remove ${m.name}`}
                        >
                          <CloseIcon style={{ fontSize: "13px" }} />
                        </button>
                      </span>
                    ))}
                    <button
                      type="button"
                      onClick={(e) => openAddPicker(e, role.id, level)}
                      className="inline-flex items-center gap-1 rounded-lg border border-dashed border-violet-300 px-2.5 py-1 text-xs font-medium text-violet-700 transition-colors hover:bg-violet-50"
                    >
                      <AddIcon style={{ fontSize: "14px" }} /> Add modules
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <ArrowUpwardIcon style={{ fontSize: "14px" }} />
            Each level also requires everything in the levels below it.
          </div>
          {availableHalfSteps.length > 0 && (
            <button
              type="button"
              onClick={(e) => {
                setHalfAnchor(e.currentTarget);
                setHalfRoleId(role.id);
              }}
              className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-medium text-slate-500 transition-colors hover:text-violet-700"
            >
              <AddIcon style={{ fontSize: "14px" }} /> Add a half-star level
            </button>
          )}
        </div>
      </div>
    );
  };

  const halfRole = roles.find((r) => r.id === halfRoleId);
  const halfStepOptions = halfRole ? getAvailableHalfSteps(halfRole) : [];

  return (
    <>
      <div className="px-6 pb-10 pt-6">
        {/* What this is + the cumulative rule, stated up front. */}
        <div className="mb-4 flex items-start gap-3 rounded-xl border border-violet-100 bg-violet-50 px-4 py-3 text-sm text-slate-700">
          <InfoOutlinedIcon
            className="mt-0.5 shrink-0 text-violet-600"
            style={{ fontSize: "18px" }}
          />
          <div>
            Proficiency stars show how trained someone is for a role — they
            appear on the schedule and in training reports. For each role, set
            what each star level requires. An employee earns a level by
            completing{" "}
            <span className="font-semibold">
              all of its modules and every level below it
            </span>
            .
          </div>
        </div>

        {roles.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-white px-6 py-12 text-center">
            <WorkIcon
              className="mx-auto mb-3 text-slate-300"
              style={{ fontSize: "32px" }}
            />
            <p className="text-sm font-medium text-slate-900">No roles yet</p>
            <p className="mt-1 text-sm text-slate-500">
              Create roles under Roles &amp; Departments to set proficiency
              levels for them.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-200 overflow-hidden rounded-xl border border-slate-200 bg-white">
            {roles.map((role) => {
              const expanded = expandedRoleId === role.id;
              const { maxLevel, levelCount, moduleCount } =
                getRoleSummary(role);
              return (
                <div key={role.id}>
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedRoleId(expanded ? null : role.id)
                    }
                    className="flex w-full items-center justify-between px-5 py-4 text-left transition-colors hover:bg-slate-50"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      {expanded ? (
                        <ExpandMoreIcon
                          className="shrink-0 text-slate-400"
                          style={{ fontSize: "20px" }}
                        />
                      ) : (
                        <ChevronRightIcon
                          className="shrink-0 text-slate-400"
                          style={{ fontSize: "20px" }}
                        />
                      )}
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-violet-100">
                        <WorkIcon
                          className="text-violet-600"
                          style={{ fontSize: "16px" }}
                        />
                      </div>
                      <div className="truncate text-sm font-semibold text-slate-900">
                        {role.role_name}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-4">
                      {moduleCount > 0 ? (
                        <>
                          <Rating
                            value={maxLevel}
                            max={maxStars}
                            precision={0.5}
                            readOnly
                            size="small"
                          />
                          <span className="hidden text-xs text-slate-500 sm:inline">
                            {levelCount} {levelCount === 1 ? "level" : "levels"}{" "}
                            · {moduleCount}{" "}
                            {moduleCount === 1 ? "module" : "modules"}
                          </span>
                        </>
                      ) : (
                        <span className="text-xs text-slate-400">
                          Not set up yet
                        </span>
                      )}
                    </div>
                  </button>
                  {expanded && renderLadder(role)}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add-modules checklist picker */}
      <Menu
        anchorEl={addAnchor}
        open={Boolean(addAnchor)}
        onClose={closeAddPicker}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { width: 300, mt: 1 } }}
        MenuListProps={{ sx: { py: 0 } }}
      >
        <div className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          Add modules{addContext ? ` to ${addContext.level}★` : ""}
        </div>
        <div className="px-3 pb-2">
          <div className="relative">
            <SearchIcon
              className="absolute left-2 top-1/2 -translate-y-1/2 transform text-slate-400"
              style={{ fontSize: "18px" }}
            />
            <input
              type="text"
              value={pickerSearch}
              onChange={(e) => setPickerSearch(e.target.value)}
              placeholder="Search modules..."
              className="h-9 w-full rounded-lg border border-slate-200 pl-8 pr-3 text-sm outline-none transition-colors focus:border-violet-500"
            />
          </div>
        </div>
        <Divider />
        <div style={{ maxHeight: 240, overflowY: "auto" }}>
          {pickerModules.length === 0 ? (
            <div className="px-4 py-6 text-center text-xs text-slate-400">
              No modules available to add.
            </div>
          ) : (
            pickerModules.map((m) => (
              <MenuItem
                key={m.uuid}
                onClick={() => togglePick(m.uuid)}
                sx={{ py: 0.25 }}
              >
                <Checkbox
                  checked={pickerChecked.includes(m.uuid)}
                  size="small"
                  sx={{
                    color: "#6d28d9",
                    "&.Mui-checked": { color: "#6d28d9" },
                    py: 0.5,
                  }}
                />
                <ListItemText
                  primaryTypographyProps={{ fontSize: 14, color: "#334155" }}
                >
                  {m.name}
                </ListItemText>
              </MenuItem>
            ))
          )}
        </div>
        <Divider />
        <div className="flex items-center justify-end gap-2 px-3 py-2">
          <button
            type="button"
            onClick={closeAddPicker}
            className="rounded-lg px-3 py-1.5 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submitAdd}
            disabled={pickerChecked.length === 0}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              pickerChecked.length
                ? "bg-violet-600 text-white hover:bg-violet-700"
                : "cursor-not-allowed bg-slate-200 text-slate-400"
            }`}
          >
            Add{pickerChecked.length ? ` ${pickerChecked.length}` : ""}
          </button>
        </div>
      </Menu>

      {/* Create a new half-star level */}
      <Menu
        anchorEl={halfAnchor}
        open={Boolean(halfAnchor)}
        onClose={() => {
          setHalfAnchor(null);
          setHalfRoleId(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { minWidth: 200, mt: 1 } }}
      >
        <div className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          Add a half-star level
        </div>
        {halfStepOptions.length === 0 ? (
          <div className="px-4 py-3 text-center text-xs text-slate-400">
            No half-star levels available.
          </div>
        ) : (
          halfStepOptions.map((h) => (
            <MenuItem
              key={h}
              onClick={() => {
                revealHalfLevel(halfRoleId, h);
                setHalfAnchor(null);
                setHalfRoleId(null);
              }}
              sx={{ py: 0.5 }}
            >
              <Rating
                value={h}
                max={maxStars}
                precision={0.5}
                readOnly
                size="small"
              />
              <span className="ml-2 text-sm text-slate-700">{h} stars</span>
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
}

export default RoleProficiencyThresholdsDisplay;
